/**
 * OrdersManagement - Manage dining orders
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ShoppingCart, Plus, Clock, Check, ChefHat, 
  DollarSign, User, Phone, XCircle, Printer
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending', icon: Clock },
  preparing: { color: 'bg-orange-100 text-orange-800', label: 'Preparing', icon: ChefHat },
  ready: { color: 'bg-blue-100 text-blue-800', label: 'Ready', icon: Check },
  served: { color: 'bg-green-100 text-green-800', label: 'Served', icon: Check },
  paid: { color: 'bg-gray-100 text-gray-800', label: 'Paid', icon: DollarSign },
  cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled', icon: XCircle }
};

const OrdersManagement = () => {
  const queryClient = useQueryClient();
  const [showNewOrderDialog, setShowNewOrderDialog] = useState(false);
  const [activeStatus, setActiveStatus] = useState('active');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch orders based on status
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['dining-orders', activeStatus],
    queryFn: async () => {
      let url = `${API_URL}/api/admin/dining/orders?today_only=true`;
      if (activeStatus === 'active') {
        // Fetch pending + preparing + ready
        const [pending, preparing, ready] = await Promise.all([
          fetch(`${url}&status=pending`, { headers }).then(r => r.json()),
          fetch(`${url}&status=preparing`, { headers }).then(r => r.json()),
          fetch(`${url}&status=ready`, { headers }).then(r => r.json())
        ]);
        return [...pending, ...preparing, ...ready];
      } else if (activeStatus === 'completed') {
        const [served, paid] = await Promise.all([
          fetch(`${url}&status=served`, { headers }).then(r => r.json()),
          fetch(`${url}&status=paid`, { headers }).then(r => r.json())
        ]);
        return [...served, ...paid];
      } else {
        const res = await fetch(`${url}&status=${activeStatus}`, { headers });
        return res.json();
      }
    },
    refetchInterval: 10000
  });

  // Fetch menu items for new order
  const { data: menuItems = [] } = useQuery({
    queryKey: ['dining-menu-available'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/menu?available_only=true`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch tables
  const { data: tables = [] } = useQuery({
    queryKey: ['dining-tables'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/tables`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Create order
  const createOrderMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/dining/orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create order');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-orders']);
      queryClient.invalidateQueries(['dining-tables']);
      queryClient.invalidateQueries(['dining-stats']);
      setShowNewOrderDialog(false);
      toast.success('Order created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update order status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ orderId, status, data = {} }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/orders/${orderId}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status, ...data })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update status');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-orders']);
      queryClient.invalidateQueries(['dining-tables']);
      queryClient.invalidateQueries(['dining-stats']);
      toast.success('Order updated');
    },
    onError: (error) => toast.error(error.message)
  });

  const availableTables = tables.filter(t => t.status === 'available');

  // New Order Form Component
  const NewOrderForm = ({ onSubmit, onCancel, isLoading }) => {
    const [orderData, setOrderData] = useState({
      order_type: 'dine_in',
      table_id: '',
      customer_name: '',
      customer_phone: '',
      items: [],
      notes: '',
      discount_percent: 0,
      payment_method: 'cash'
    });

    const addItem = (menuItem) => {
      const existing = orderData.items.find(i => i.menu_item_id === menuItem.id);
      if (existing) {
        setOrderData({
          ...orderData,
          items: orderData.items.map(i =>
            i.menu_item_id === menuItem.id
              ? { ...i, quantity: i.quantity + 1 }
              : i
          )
        });
      } else {
        setOrderData({
          ...orderData,
          items: [...orderData.items, {
            menu_item_id: menuItem.id,
            name: menuItem.name,
            price: menuItem.special_price || menuItem.price,
            quantity: 1,
            notes: ''
          }]
        });
      }
    };

    const updateItemQty = (menuItemId, qty) => {
      if (qty <= 0) {
        setOrderData({
          ...orderData,
          items: orderData.items.filter(i => i.menu_item_id !== menuItemId)
        });
      } else {
        setOrderData({
          ...orderData,
          items: orderData.items.map(i =>
            i.menu_item_id === menuItemId ? { ...i, quantity: qty } : i
          )
        });
      }
    };

    const subtotal = orderData.items.reduce((sum, i) => sum + (i.price * i.quantity), 0);
    const discount = subtotal * (orderData.discount_percent / 100);
    const total = subtotal - discount;

    return (
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Order Type</label>
            <Select
              value={orderData.order_type}
              onValueChange={(value) => setOrderData({ ...orderData, order_type: value, table_id: '' })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dine_in">Dine In</SelectItem>
                <SelectItem value="takeaway">Takeaway</SelectItem>
                <SelectItem value="delivery">Delivery</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {orderData.order_type === 'dine_in' && (
            <div>
              <label className="text-sm font-medium">Table</label>
              <Select
                value={orderData.table_id}
                onValueChange={(value) => setOrderData({ ...orderData, table_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select table" />
                </SelectTrigger>
                <SelectContent>
                  {availableTables.map((table) => (
                    <SelectItem key={table.id} value={table.id}>
                      Table {table.table_number} ({table.capacity} seats)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {orderData.order_type !== 'dine_in' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Customer Name</label>
              <Input
                value={orderData.customer_name}
                onChange={(e) => setOrderData({ ...orderData, customer_name: e.target.value })}
                placeholder="Name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input
                value={orderData.customer_phone}
                onChange={(e) => setOrderData({ ...orderData, customer_phone: e.target.value })}
                placeholder="Phone number"
              />
            </div>
          </div>
        )}

        {/* Menu Items Selection */}
        <div>
          <label className="text-sm font-medium mb-2 block">Add Items</label>
          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
            {menuItems.map((item) => (
              <Button
                key={item.id}
                type="button"
                variant="outline"
                size="sm"
                className="justify-between h-auto py-2"
                onClick={() => addItem(item)}
              >
                <span className="truncate">{item.name}</span>
                <span className="text-green-600 ml-2">${(item.special_price || item.price)?.toFixed(2)}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Selected Items */}
        {orderData.items.length > 0 && (
          <div className="border rounded-lg p-3 space-y-2">
            <label className="text-sm font-medium">Order Items</label>
            {orderData.items.map((item) => (
              <div key={item.menu_item_id} className="flex items-center justify-between">
                <span className="text-sm">{item.name}</span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 w-6 p-0"
                    onClick={() => updateItemQty(item.menu_item_id, item.quantity - 1)}
                  >-</Button>
                  <span className="w-6 text-center">{item.quantity}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 w-6 p-0"
                    onClick={() => updateItemQty(item.menu_item_id, item.quantity + 1)}
                  >+</Button>
                  <span className="w-16 text-right text-sm font-medium">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {orderData.discount_percent > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount ({orderData.discount_percent}%)</span>
                  <span>-${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold mt-1">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Discount %</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={orderData.discount_percent}
              onChange={(e) => setOrderData({ ...orderData, discount_percent: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Payment Method</label>
            <Select
              value={orderData.payment_method}
              onValueChange={(value) => setOrderData({ ...orderData, payment_method: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="mobile">Mobile Pay</SelectItem>
                <SelectItem value="account">Account</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Notes</label>
          <Textarea
            value={orderData.notes}
            onChange={(e) => setOrderData({ ...orderData, notes: e.target.value })}
            placeholder="Special instructions"
            rows={2}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={() => onSubmit(orderData)}
            disabled={isLoading || orderData.items.length === 0}
            data-testid="submit-order-btn"
          >
            {isLoading ? 'Creating...' : `Create Order ($${total.toFixed(2)})`}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="orders-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Orders</h2>
          <p className="text-sm text-gray-500">Manage today's orders</p>
        </div>
        <Button onClick={() => setShowNewOrderDialog(true)} data-testid="new-order-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Order
        </Button>
      </div>

      {/* Status Tabs */}
      <Tabs value={activeStatus} onValueChange={setActiveStatus}>
        <TabsList>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
          <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading orders...</div>
      ) : orders.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No {activeStatus} orders</h3>
            <p className="text-sm text-gray-500 mb-4">
              {activeStatus === 'active' ? 'Create a new order to get started' : `No ${activeStatus} orders today`}
            </p>
            {activeStatus === 'active' && (
              <Button onClick={() => setShowNewOrderDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Order
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orders.map((order) => {
            const status = statusConfig[order.status] || statusConfig.pending;
            const StatusIcon = status.icon;
            
            return (
              <Card key={order.id} data-testid={`order-card-${order.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{order.order_number}</CardTitle>
                    <Badge className={status.color}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {status.label}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500">
                    {order.order_type === 'dine_in' ? `Table` : order.order_type}
                    {order.customer_name && ` • ${order.customer_name}`}
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Order Items */}
                  <div className="space-y-1">
                    {order.items?.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>{item.quantity}x {item.name}</span>
                        <span>${item.total?.toFixed(2)}</span>
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <p className="text-xs text-gray-400">+{order.items.length - 3} more items</p>
                    )}
                  </div>

                  {/* Total */}
                  <div className="flex justify-between font-bold border-t pt-2">
                    <span>Total</span>
                    <span>${order.total?.toFixed(2)}</span>
                  </div>

                  {/* Time */}
                  <p className="text-xs text-gray-400">
                    {new Date(order.created_at).toLocaleTimeString()}
                  </p>

                  {/* Actions based on status */}
                  <div className="flex gap-2 pt-2 border-t">
                    {order.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={() => updateStatusMutation.mutate({ orderId: order.id, status: 'preparing' })}
                        >
                          <ChefHat className="h-4 w-4 mr-1" />
                          Start Preparing
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateStatusMutation.mutate({ orderId: order.id, status: 'cancelled' })}
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {order.status === 'preparing' && (
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => updateStatusMutation.mutate({ orderId: order.id, status: 'ready' })}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Mark Ready
                      </Button>
                    )}
                    {order.status === 'ready' && (
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => updateStatusMutation.mutate({ orderId: order.id, status: 'served' })}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Mark Served
                      </Button>
                    )}
                    {order.status === 'served' && (
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => updateStatusMutation.mutate({ orderId: order.id, status: 'paid' })}
                      >
                        <DollarSign className="h-4 w-4 mr-1" />
                        Mark Paid
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* New Order Dialog */}
      <Dialog open={showNewOrderDialog} onOpenChange={setShowNewOrderDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Order</DialogTitle>
          </DialogHeader>
          <NewOrderForm
            onSubmit={(data) => createOrderMutation.mutate(data)}
            onCancel={() => setShowNewOrderDialog(false)}
            isLoading={createOrderMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersManagement;
